/**
 * Product Media V2 - BEM JavaScript with Swiper.js
 * Handles product media gallery with thumbnail navigation
 * Uses Swiper.js for thumbnail carousel
 */

class ProductMediaV2 {
  constructor(container) {
    this.container = container;
    this.mainSwiperContainer = container.querySelector('[data-main-swiper]');
    this.thumbnails = container.querySelectorAll('.product-media__thumbnail');
    this.thumbsSwiperContainer = container.querySelector('[data-thumbnails-swiper]');
    this.prevButton = container.querySelector('[data-thumb-nav="prev"]');
    this.nextButton = container.querySelector('[data-thumb-nav="next"]');

    this.thumbsSwiper = null;
    this.mainSwiper = null;

    this.init();
  }

  init() {
    if (typeof Swiper === 'undefined') {
      console.error('Swiper.js is not loaded');
      return;
    }

    // Initialize thumbs swiper first
    if (this.thumbnails.length > 1) {
      this.initThumbsSwiper();
    }

    // Initialize main swiper
    this.initMainSwiper();
  }

  /**
   * Initialize Swiper.js for thumbnails
   * Desktop: vertical carousel
   * Mobile: horizontal carousel
   */
  initThumbsSwiper() {
    if (!this.thumbsSwiperContainer) return;

    const isDesktop = window.innerWidth >= 990;

    this.thumbsSwiper = new Swiper(this.thumbsSwiperContainer, {
      // Direction based on screen size
      direction: isDesktop ? 'vertical' : 'horizontal',

      // Slides per view
      slidesPerView: 'auto',
      spaceBetween: isDesktop ? 9 : 6,

      // Free mode for smooth scrolling
      freeMode: false,

      // Watch for clicks
      watchSlidesProgress: true,
      slideToClickedSlide: true,

      // Navigation
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },

      // Disable navigation buttons when at start/end
      watchOverflow: true,

      // Mouse wheel (desktop only)
      mousewheel: isDesktop ? {
        forceToAxis: true,
      } : false,

      // Touch resistance
      touchRatio: 1,

      // Breakpoints
      breakpoints: {
        // Mobile (default)
        0: {
          direction: 'horizontal',
          spaceBetween: 6,
          mousewheel: false,
        },
        // Desktop
        990: {
          direction: 'vertical',
          spaceBetween: 9,
          mousewheel: {
            forceToAxis: true,
          },
        },
      },
    });

    // Update navigation button states
    this.updateNavigationState();

    // Listen to Swiper events
    this.thumbsSwiper.on('slideChange', () => {
      this.updateNavigationState();
    });
  }

  /**
   * Initialize main Swiper.js slider
   * Connected to thumbs swiper
   */
  initMainSwiper() {
    if (!this.mainSwiperContainer) return;

    this.mainSwiper = new Swiper(this.mainSwiperContainer, {
      spaceBetween: 10,

      // Connect with thumbs swiper
      thumbs: {
        swiper: this.thumbsSwiper,
      },

      // Loop if more than one slide
      loop: false,

      // Lazy loading
      lazy: true,

      // Effect
      effect: 'slide',

      // Speed
      speed: 300,

      // Disable touch/swipe - only click navigation
      touchRatio: 0,
      simulateTouch: false,
      allowTouchMove: false,
      grabCursor: false,

      // Enable keyboard
      keyboard: {
        enabled: true,
      },
    });

    // Listen to slide change to update active thumbnail class
    this.mainSwiper.on('slideChange', () => {
      this.updateActiveThumbnail();
    });

    // Add click event on main image to navigate to next slide
    this.addClickNavigation();

    // Initial active state
    this.updateActiveThumbnail();
  }

  /**
   * Add click event listener to navigate through slides
   */
  addClickNavigation() {
    if (!this.mainSwiper) return;

    const slides = this.mainSwiperContainer.querySelectorAll('.swiper-slide');

    slides.forEach((slide) => {
      slide.style.cursor = 'pointer';

      slide.addEventListener('click', (e) => {
        // Don't navigate if clicking on video controls
        if (e.target.tagName === 'VIDEO' || e.target.closest('video')) {
          return;
        }

        // Go to next slide, or loop back to first
        if (this.mainSwiper.isEnd) {
          this.mainSwiper.slideTo(0);
        } else {
          this.mainSwiper.slideNext();
        }
      });
    });
  }

  /**
   * Update active class on thumbnails based on main swiper
   */
  updateActiveThumbnail() {
    if (!this.mainSwiper) return;

    const activeIndex = this.mainSwiper.activeIndex;

    // Remove active class from all thumbnails
    this.thumbnails.forEach(thumb => {
      thumb.classList.remove('product-media__thumbnail--active');
    });

    // Add active class to current thumbnail
    const activeThumbnail = this.thumbnails[activeIndex];
    if (activeThumbnail) {
      activeThumbnail.classList.add('product-media__thumbnail--active');
    }
  }

  /**
   * Update navigation button disabled state
   */
  updateNavigationState() {
    if (!this.thumbsSwiper) return;

    // Update prev button
    if (this.prevButton) {
      if (this.thumbsSwiper.isBeginning) {
        this.prevButton.disabled = true;
      } else {
        this.prevButton.disabled = false;
      }
    }

    // Update next button
    if (this.nextButton) {
      if (this.thumbsSwiper.isEnd) {
        this.nextButton.disabled = true;
      } else {
        this.nextButton.disabled = false;
      }
    }
  }

  /**
   * Destroy Swiper instances
   */
  destroy() {
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
    }
  }
}

// Initialize all product media galleries
document.addEventListener('DOMContentLoaded', () => {
  const productMediaContainers = document.querySelectorAll('[data-product-media]');

  productMediaContainers.forEach(container => {
    new ProductMediaV2(container);
  });
});

// Re-initialize on window resize (to handle desktop ↔ mobile transitions)
let productMediaResizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(productMediaResizeTimer);
  productMediaResizeTimer = setTimeout(() => {
    // Destroy and re-initialize on breakpoint cross
    const productMediaContainers = document.querySelectorAll('[data-product-media]');
    productMediaContainers.forEach(container => {
      // Store existing instance if available
      if (container.productMediaInstance) {
        container.productMediaInstance.destroy();
      }
      container.productMediaInstance = new ProductMediaV2(container);
    });
  }, 250);
});

// Re-initialize on section reload (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const productMediaContainers = event.target.querySelectorAll('[data-product-media]');
    productMediaContainers.forEach(container => {
      new ProductMediaV2(container);
    });
  });
}
